import {
  useMediaQuery,
  Box,
  Typography,
  IconButton,
  TextField,
  Pagination,
  CircularProgress,
} from "@mui/material";
import AdminSidebar from "./AdminSidebar";
import { Search } from "@mui/icons-material";
import color from "../../components/utils/Colors";
import AdminCourseCard from "../../components/cards/AdminCourseCard";
import { useEffect, useMemo, useState } from "react";
import {
  AdminCourseGroup,
  fetchAdminCoursesGrouped,
} from "../../services/adminData";

const AdminCourse = () => {
  const isSmallScreen = useMediaQuery("(max-width:1050px)");
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [courseGroups, setCourseGroups] = useState<AdminCourseGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const rowsPerPage = 4;

  useEffect(() => {
    setLoading(true);
    fetchAdminCoursesGrouped()
      .then(setCourseGroups)
      .finally(() => setLoading(false));
  }, []);

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return courseGroups;
    const query = searchQuery.toLowerCase();
    return courseGroups.filter(
      (group) =>
        group.name.toLowerCase().includes(query) ||
        group.email.toLowerCase().includes(query) ||
        group.courses.some((course) =>
          course.title.toLowerCase().includes(query),
        ),
    );
  }, [courseGroups, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const startIndex = (page - 1) * rowsPerPage;
  const paginatedGroups = filteredGroups.slice(
    startIndex,
    startIndex + rowsPerPage,
  );

  const handleChangePage = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const totalCourses = courseGroups.reduce(
    (sum, group) => sum + group.courses.length,
    0,
  );

  return (
    <Box
      sx={{
        display: "flex",
        backgroundImage: "url('/images/logo 2.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        height: "auto",
        margin: isSmallScreen ? "10px" : "30px",
        marginTop: "0px",
        paddingTop: "20px",
        gap: "3%",
        borderRadius: "10px",
        flexDirection: isSmallScreen ? "column" : "row",
      }}
    >
      <AdminSidebar />

      <Box sx={{ flex: 1, borderRadius: "16px" }}>
        <TextField
          className="input-root2"
          placeholder="Search..."
          size="small"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton>
                <Search />
              </IconButton>
            ),
          }}
          sx={{ width: { xs: "100%", md: "auto" } }}
        />

        <Box
          sx={{
            position: "relative",
            background: color.firstColor,
            borderRadius: "16px",
            height: "fit-content",
            my: 4,
          }}
        >
          <Box
            component="img"
            src="/assets/Welcome Bar.svg"
            alt="Welcome Bar"
            sx={{ width: "100%", height: 200, objectFit: "contain" }}
          />
          <Typography
            sx={{
              color: "white",
              position: "absolute",
              top: { xs: "18%", sm: "25%" },
              left: "3%",
              fontSize: "32px",
              fontWeight: "bold",
            }}
          >
            Courses
          </Typography>
          <Typography
            sx={{
              color: "white",
              position: "absolute",
              left: "3%",
              maxWidth: "62%",
              top: { xs: "44%", sm: "50%" },
            }}
          >
            Review {totalCourses} course{totalCourses === 1 ? "" : "s"} from{" "}
            {courseGroups.length} teacher
            {courseGroups.length === 1 ? "" : "s"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            justifyContent: isSmallScreen ? "center" : "space-between",
            border: "solid 1.5px",
            borderColor: color.firstColor,
            p: isSmallScreen ? 1 : 4,
            pt: 4,
            borderRadius: "12px",
            background: "white",
          }}
        >
          <h2
            style={{
              color: color.textColor,
              textAlign: "left",
              width: "100%",
              paddingLeft: "10px",
              marginTop: "5px",
            }}
          >
            Courses List
          </h2>

          {loading ? (
            <Box display="flex" justifyContent="center" width="100%" py={6}>
              <CircularProgress sx={{ color: color.firstColor }} />
            </Box>
          ) : paginatedGroups.length === 0 ? (
            <Typography sx={{ width: "100%", textAlign: "center", py: 4 }}>
              No courses found.
            </Typography>
          ) : (
            paginatedGroups.map((group) => (
              <AdminCourseCard
                key={group.teacherId}
                user={{
                  name: group.name,
                  email: group.email,
                  phone: group.phone,
                  isVerified: group.isVerified,
                  courses: group.courses,
                }}
              />
            ))
          )}

          {!loading && filteredGroups.length > rowsPerPage && (
            <Pagination
              count={Math.ceil(filteredGroups.length / rowsPerPage)}
              page={page}
              onChange={handleChangePage}
              style={{
                marginTop: "100px",
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminCourse;
